"use client";

import { useState } from "react";

const CHAINS = [
  { id: 1, name: "Ethereum" },
  { id: 56, name: "BNB Chain" },
  { id: 137, name: "Polygon" },
  { id: 42161, name: "Arbitrum" },
  { id: 10, name: "Optimism" },
  { id: 43114, name: "Avalanche" },
  { id: 8453, name: "Base" },
  { id: 1151111081099710, name: "Solana" },
];

const BASE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://forgeswap.fun";

export default function WhitelabelPage() {
  const [form, setForm] = useState({
    projectName: "",
    widgetName: "",
    tokenAddress: "",
    tokenSymbol: "",
    chainId: "1",
    tokenLogoUrl: "",
    heroImageUrl: "",
    primaryColor: "#f9cd4f",
    secondaryColor: "#d4a012",
    backgroundType: "solid",
    backgroundValue: "#111111",
    backgroundValue2: "#1a1a2e",
    textColor: "#ffffff",
    projectWebsite: "",
    contactName: "",
    contactEmail: "",
    contactTwitter: "",
    contactTelegram: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (key: string, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    if (!form.projectName || !form.tokenAddress || !form.chainId) {
      setError("Please fill in Project Name, Token Address and Chain.");
      return;
    }
    if (!form.contactEmail && !form.contactTwitter && !form.contactTelegram) {
      setError("Please provide at least one contact method — Email, Twitter, or Telegram.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/whitelabel/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, chainId: parseInt(form.chainId) }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        setError(data.error || "Something went wrong.");
      }
    } catch (e) {
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Live preview styles
  const previewBg =
    form.backgroundType === "gradient"
      ? `linear-gradient(135deg, ${form.backgroundValue}, ${form.backgroundValue2})`
      : form.backgroundType === "image"
      ? `url(${form.backgroundValue}) center/cover`
      : form.backgroundValue;

  if (submitted) {
    return (
      <div style={{ background: "#111", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif" }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>🎉</div>
          <h1 style={{ color: "#f9cd4f", fontSize: 32, fontWeight: 700, marginBottom: 16 }}>Request Submitted!</h1>
          <p style={{ color: "#aaa", fontSize: 16, marginBottom: 8 }}>We've received your widget request and will reach out via your provided contact method.</p>
          <p style={{ color: "#aaa", fontSize: 16 }}>We'll review your details and get back to you within 24-48 hours.</p>
          <a href={BASE_URL} style={{ display: "inline-block", marginTop: 32, background: "#f9cd4f", color: "#111", padding: "12px 32px", borderRadius: 10, fontWeight: 700, textDecoration: "none" }}>Back to ForgeSwap</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#111", minHeight: "100vh", color: "#fff", fontFamily: "Inter, sans-serif", overflow: "hidden", maxWidth: "100vw" }}>
      {/* Hero explanation */}
      <div style={{ background: "linear-gradient(135deg, #1a1a1a, #111)", borderBottom: "1px solid #222", padding: "32px 16px", overflow: "hidden" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ color: "#f9cd4f", fontSize: "clamp(22px, 5vw, 32px)", fontWeight: 800, marginBottom: 16 }}>
            Your Token. Your Brand. Your Swap.
          </h2>
          <p style={{ color: "#ccc", fontSize: 16, lineHeight: 1.8, marginBottom: 40, maxWidth: 700, margin: "0 auto 40px" }}>
            Get a fully branded swap widget that lives on your website and lets your community buy your token instantly — without ever leaving your site.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 16, textAlign: "left" }}>
            {[
              { color: "#f9cd4f", title: "Fully Branded", desc: "Your logo, your colors, your name. The widget looks like it was built just for your project." },
              { color: "#22c55e", title: "Easy to Add", desc: "One line of code on your website. Works on any site — no technical knowledge needed." },
              { color: "#3b82f6", title: "Earn Revenue Share", desc: "Every swap through your widget generates fees. We share a percentage back to your project twice a week." },
              { color: "#a855f7", title: "Multi-Chain", desc: "Supports Ethereum, Solana, BNB Chain, Polygon, Base, Arbitrum and more." },
              { color: "#f97316", title: "All Major Wallets", desc: "MetaMask, Phantom, Coinbase Wallet, WalletConnect and more — your users connect with whatever they already have." },
              { color: "#06b6d4", title: "Volume Tracking", desc: "See your swap volume and fees generated through your widget. We handle all the tracking and payouts." },
            ].map((item) => (
              <div key={item.title} style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 12, padding: 20 }}>
                <div style={{ width: 4, height: 32, borderRadius: 2, background: item.color, marginBottom: 12 }} />
                <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{item.title}</h3>
                <p style={{ color: "#888", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 40, padding: 24, background: "#1a1a1a", borderRadius: 12, border: "1px solid #333", textAlign: "left" }}>
            <h3 style={{ color: "#f9cd4f", fontSize: 16, fontWeight: 700, marginBottom: 12 }}>How it works</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
              {[
                { step: "1", text: "Fill out the form below with your project details and branding preferences" },
                { step: "2", text: "We review your submission and contact you within 24-48 hours" },
                { step: "3", text: "We set up your branded widget and send you a single line of code" },
                { step: "4", text: "Paste it on your site and start earning from every swap" },
              ].map((s) => (
                <div key={s.step} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#f9cd4f", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                    {s.step}
                  </div>
                  <p style={{ color: "#ccc", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px 0", display: "grid" }}>
        <div style={{ background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, padding: 28, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div style={{ display: "grid", gap: 24 }}>
            <div>
              <h2 style={{ color: "#f9cd4f", fontSize: "clamp(24px, 4vw, 32px)", fontWeight: 800, margin: "0 0 12px" }}>Whitelabel Swap for Your Website</h2>
              <p style={{ color: "#fff", fontSize: 16, lineHeight: 1.8, margin: 0, maxWidth: 900 }}>
                Add a fully branded swap widget to your website in minutes. Your community buys, sells, and swaps tokens directly on your site — and you earn every time they do.
              </p>
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700, margin: 0 }}>What's included</h3>
              <div style={{ display: "grid", gap: 10 }}>
                {[
                  "Custom branded swap widget for your website",
                  "Swap across 30+ blockchains",
                  "Apple Pay, Google Pay & credit/debit card support built in",
                  "0.5% revenue share on all transactions through your widget",
                  "Revenue paid out 2x weekly",
                ].map((item) => (
                  <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "rgba(17,17,17,0.9)", border: "1px solid rgba(249,205,79,0.14)", borderRadius: 14, padding: "14px 16px" }}>
                    <span style={{ color: "#f9cd4f", fontSize: 18, lineHeight: 1, fontWeight: 700 }}>✓</span>
                    <span style={{ color: "#fff", fontSize: 15, lineHeight: 1.7 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ justifySelf: "start", background: "rgba(249,205,79,0.12)", border: "1px solid rgba(249,205,79,0.4)", color: "#f9cd4f", borderRadius: 999, padding: "14px 20px", fontSize: 18, fontWeight: 800, letterSpacing: 0.2 }}>
              $500 setup fee · $50/month
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 32 }}>
        {/* Form */}
        <div>
          <h2 style={{ color: "#f9cd4f", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Widget Request</h2>
          <p style={{ color: "#888", fontSize: 14, marginBottom: 32 }}>Fill out the form and we'll set up your branded swap widget. We'll contact you within 24-48 hours.</p>

          {error && (
            <div style={{ background: "#ef444422", border: "1px solid #ef4444", color: "#ef4444", padding: "12px 16px", borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
              {error}
            </div>
          )}

          {/* Project Info */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Project Info</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { key: "projectName", label: "Project Name *", placeholder: "e.g. PEPE Token" },
                { key: "widgetName", label: "Widget Name", placeholder: "e.g. PEPE Swap" },
                { key: "tokenAddress", label: "Token Address *", placeholder: "0x... or Solana address" },
                { key: "tokenSymbol", label: "Token Symbol", placeholder: "e.g. PEPE" },
                { key: "projectWebsite", label: "Website", placeholder: "https://..." },
                {
                  key: "tokenLogoUrl",
                  label: "Token Logo URL",
                  placeholder: "https://...",
                  tooltip: "Get a direct image URL by: 1) Uploading to imgur.com (free) 2) Right-clicking your logo on CoinGecko or CoinMarketCap 3) Right-clicking any image on your website → Copy Image Address",
                },
                {
                  key: "heroImageUrl",
                  label: "Hero/Banner Image URL (optional)",
                  placeholder: "https://...",
                  tooltip: "Large banner image for the top of your widget. Recommended 600x200px. Get a URL by uploading to imgur.com (free) or right-clicking an image on your site → Copy Image Address",
                },
              ].map((field) => (
                <div key={field.key} style={field.key === "tokenAddress" ? { gridColumn: "span 2" } : {}}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#888", fontSize: 12, marginBottom: 4 }}>
                    {field.label}
                    {field.tooltip && (
                      <span title={field.tooltip} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, background: "#333", borderRadius: "50%", fontSize: 10, color: "#aaa", cursor: "help" }}>?</span>
                    )}
                  </label>
                  <input
                    value={(form as any)[field.key]}
                    onChange={(e) => update(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                  />
                  {(field.key === "tokenLogoUrl" || field.key === "heroImageUrl") && (
                    <div style={{ marginTop: 4 }}>
                      <a
                        href="https://imgur.com/upload"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#f9cd4f", fontSize: 11, textDecoration: "none" }}
                      >
                        Upload to Imgur to get a URL →
                      </a>
                    </div>
                  )}
                </div>
              ))}
              <div>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 4 }}>Chain *</label>
                <select
                  value={form.chainId}
                  onChange={(e) => update("chainId", e.target.value)}
                  style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14 }}
                >
                  {CHAINS.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Branding */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Branding</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 4 }}>Primary Color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={form.primaryColor} onChange={(e) => update("primaryColor", e.target.value)} style={{ width: 40, height: 40, border: "none", background: "none", cursor: "pointer" }} />
                  <input value={form.primaryColor} onChange={(e) => update("primaryColor", e.target.value)} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14 }} />
                </div>
              </div>
              <div>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 4 }}>Secondary Color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={form.secondaryColor} onChange={(e) => update("secondaryColor", e.target.value)} style={{ width: 40, height: 40, border: "none", background: "none", cursor: "pointer" }} />
                  <input value={form.secondaryColor} onChange={(e) => update("secondaryColor", e.target.value)} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14 }} />
                </div>
              </div>
              <div>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 4 }}>Text Color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={form.textColor} onChange={(e) => update("textColor", e.target.value)} style={{ width: 40, height: 40, border: "none", background: "none", cursor: "pointer" }} />
                  <input value={form.textColor} onChange={(e) => update("textColor", e.target.value)} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14 }} />
                </div>
              </div>
              <div>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 4 }}>Background Type</label>
                <select
                  value={form.backgroundType}
                  onChange={(e) => update("backgroundType", e.target.value)}
                  style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14 }}
                >
                  <option value="solid">Solid Color</option>
                  <option value="gradient">Gradient</option>
                  <option value="image">Image URL</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 4 }}>
                  {form.backgroundType === "image" ? "Image URL" : "Background Color 1"}
                </label>
                {form.backgroundType === "image" ? (
                  <input value={form.backgroundValue} onChange={(e) => update("backgroundValue", e.target.value)} placeholder="https://..." style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }} />
                ) : (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="color" value={form.backgroundValue} onChange={(e) => update("backgroundValue", e.target.value)} style={{ width: 40, height: 40, border: "none", background: "none", cursor: "pointer" }} />
                    <input value={form.backgroundValue} onChange={(e) => update("backgroundValue", e.target.value)} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14 }} />
                  </div>
                )}
              </div>
              {form.backgroundType === "gradient" && (
                <div>
                  <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 4 }}>Background Color 2</label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="color" value={form.backgroundValue2} onChange={(e) => update("backgroundValue2", e.target.value)} style={{ width: 40, height: 40, border: "none", background: "none", cursor: "pointer" }} />
                    <input value={form.backgroundValue2} onChange={(e) => update("backgroundValue2", e.target.value)} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14 }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact */}
          <div style={{ marginBottom: 32 }}>
            <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Contact Info</h3>
            <p style={{ color: "#888", fontSize: 12, marginBottom: 12 }}>(at least one required)</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { key: "contactName", label: "Your Name", placeholder: "John Smith" },
                { key: "contactEmail", label: "Email Address", placeholder: "you@project.com" },
                { key: "contactTwitter", label: "Twitter / X", placeholder: "@yourhandle" },
                { key: "contactTelegram", label: "Telegram", placeholder: "@yourhandle" },
              ].map((field) => (
                <div key={field.key}>
                  <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 4 }}>{field.label}</label>
                  <input
                    value={(form as any)[field.key]}
                    onChange={(e) => update(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                  />
                </div>
              ))}
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 4 }}>Message (optional)</label>
                <textarea
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Any additional details about your project..."
                  rows={3}
                  style={{ width: "100%", background: "#1a1a1a", border: "1px solid #333", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 14, boxSizing: "border-box", resize: "vertical" }}
                />
              </div>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{ width: "100%", background: "linear-gradient(135deg, #f9cd4f, #d4a012)", color: "#111", border: "none", padding: "14px", borderRadius: 10, fontWeight: 700, fontSize: 16, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1 }}
          >
            {submitting ? "Submitting..." : "Submit Widget Request"}
          </button>
        </div>

        {/* Live Preview */}
        <div style={{ display: "block" }} className="hidden md:block">
          <div style={{ position: "sticky", top: 24, height: "fit-content" }}>
            <h2 style={{ color: "#f9cd4f", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Live Preview</h2>
            <p style={{ color: "#888", fontSize: 14, marginBottom: 24 }}>This is how your widget will look on your site.</p>
            <div style={{ borderRadius: 16, overflow: "hidden", border: "1px solid #333", background: previewBg, padding: 24, minHeight: 400 }}>
            {form.heroImageUrl && (
              <div style={{ width: "100%", height: 80, borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
                <img src={form.heroImageUrl} alt="hero" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => (e.currentTarget.style.display = "none")} />
              </div>
            )}
            {/* Widget Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
              {form.tokenLogoUrl && (
                <img src={form.tokenLogoUrl} alt="logo" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "contain" }} onError={(e) => (e.currentTarget.style.display = "none")} />
              )}
              <div>
                <h3 style={{ color: form.textColor, fontSize: 18, fontWeight: 700, margin: 0 }}>{form.widgetName || form.projectName || "Your Widget Name"}</h3>
                <p style={{ color: form.textColor, opacity: 0.6, fontSize: 12, margin: 0 }}>Swap tokens instantly</p>
              </div>
            </div>
            {/* Mock swap UI */}
            <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 12, padding: 16, marginBottom: 12 }}>
              <p style={{ color: form.textColor, opacity: 0.6, fontSize: 11, margin: "0 0 6px" }}>YOU PAY</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: form.textColor, fontSize: 24, fontWeight: 700 }}>0.00</span>
                <span style={{ background: form.primaryColor, color: "#111", padding: "6px 12px", borderRadius: 20, fontSize: 13, fontWeight: 600 }}>ETH</span>
              </div>
            </div>
            <div style={{ textAlign: "center", margin: "8px 0" }}>
              <span style={{ color: form.primaryColor, fontSize: 20 }}>⇅</span>
            </div>
            <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <p style={{ color: form.textColor, opacity: 0.6, fontSize: 11, margin: "0 0 6px" }}>YOU RECEIVE</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: form.textColor, fontSize: 24, fontWeight: 700 }}>0.00</span>
                <span style={{ background: form.primaryColor, color: "#111", padding: "6px 12px", borderRadius: 20, fontSize: 13, fontWeight: 600 }}>{form.tokenSymbol || "TOKEN"}</span>
              </div>
            </div>
            <button style={{ width: "100%", background: `linear-gradient(135deg, ${form.primaryColor}, ${form.secondaryColor})`, color: "#111", border: "none", padding: "14px", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
              Connect Wallet
            </button>
            <p style={{ textAlign: "center", marginTop: 12, fontSize: 11 }}>
              <a href={BASE_URL} target="_blank" style={{ color: form.textColor, opacity: 0.4, textDecoration: "none" }}>Powered by ForgeSwap</a>
            </p>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
